/**
 * Ambassador Program Hooks - React hooks for ambassador admin operations
 *
 * Provides React Query-based hooks for managing the ambassador program,
 * referrals, milestones, and payouts.
 *
 * Copyright: Guardrail Technologies - PROPRIETARY
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AmbassadorStatus = 'active' | 'suspended' | 'deactivated';
export type ReferralStatus = 'pending' | 'registered' | 'qualified' | 'ineligible';
export type PayoutStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type MilestoneStatus = 'pending' | 'claimed' | 'verified' | 'approved' | 'paid';

export interface Ambassador {
  id: string;
  name: string;
  email: string;
  keycloakUserId?: string;
  referralCode: string;
  status: AmbassadorStatus;
  totalReferrals: number;
  qualifiedReferrals: number;
  createdAt: string;
  updatedAt: string;
}

export interface AmbassadorListResponse {
  data: Ambassador[];
  total: number;
  page: number;
  limit: number;
}

export interface Referral {
  id: string;
  ambassadorId: string;
  ambassadorName: string;
  referredEmail: string;
  status: ReferralStatus;
  referredAt: string;
  registeredAt?: string;
  qualifiedAt?: string;
}

export interface ReferralListResponse {
  data: Referral[];
  total: number;
  page: number;
  limit: number;
}

export interface ReferralStats {
  total: number;
  qualified: number;
  pending: number;
  registered: number;
  ineligible: number;
}

export interface Milestone {
  id: string;
  ambassadorId: string;
  ambassadorName: string;
  milestone: string;
  status: MilestoneStatus;
  claimedAt?: string;
  verifiedAt?: string;
  approvedAt?: string;
  rewardAmount?: number;
}

export interface Payout {
  id: string;
  ambassadorId: string;
  ambassadorName: string;
  amount: number;
  currency: string;
  method: string;
  status: PayoutStatus;
  notes?: string;
  processedBy?: string;
  createdAt: string;
  processedAt?: string;
}

export interface PayoutListResponse {
  data: Payout[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('guardrail_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const AMBASSADOR_KEYS = {
  all: ['ambassadors'] as const,
  lists: (status?: string) => [...AMBASSADOR_KEYS.all, 'list', status ?? 'all'] as const,
  list: (page: number, status?: string) =>
    [...AMBASSADOR_KEYS.lists(status), page] as const,
  detail: (id: string) => [...AMBASSADOR_KEYS.all, 'detail', id] as const,
  referrals: (id: string, page: number) =>
    [...AMBASSADOR_KEYS.all, 'referrals', id, page] as const,
  milestones: (id: string) => [...AMBASSADOR_KEYS.all, 'milestones', id] as const,
  stats: ['ambassador-referral-stats'] as const,
  payouts: (page: number, status?: string) =>
    ['ambassador-payouts', page, status ?? 'all'] as const,
};

// ---------------------------------------------------------------------------
// Ambassador hooks
// ---------------------------------------------------------------------------

export function useAmbassadors(page: number = 1, status?: AmbassadorStatus) {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.list(page, status),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (status) params.set('status', status);
      return apiFetch<AmbassadorListResponse>(`/v2/admin/ambassadors?${params}`);
    },
    enabled: authenticated,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAmbassador(id: string) {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.detail(id),
    queryFn: () => apiFetch<Ambassador>(`/v2/admin/ambassadors/${id}`),
    enabled: authenticated && !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateAmbassador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; name: string; keycloakUserId?: string }) =>
      apiFetch<Ambassador>('/v2/admin/ambassadors', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_KEYS.all });
    },
  });
}

export function useUpdateAmbassador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; status?: AmbassadorStatus };
    }) =>
      apiFetch<Ambassador>(`/v2/admin/ambassadors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(AMBASSADOR_KEYS.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_KEYS.all });
    },
  });
}

export function useDeleteAmbassador() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/v2/admin/ambassadors/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_KEYS.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Referral hooks
// ---------------------------------------------------------------------------

export function useReferralStats() {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.stats,
    queryFn: () => apiFetch<ReferralStats>('/v2/admin/referrals/stats'),
    enabled: authenticated,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAmbassadorReferrals(id: string, page: number = 1) {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.referrals(id, page),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      return apiFetch<ReferralListResponse>(`/v2/admin/ambassadors/${id}/referrals?${params}`);
    },
    enabled: authenticated && !!id,
    staleTime: 2 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Milestone hooks
// ---------------------------------------------------------------------------

export function useAmbassadorMilestones(id: string) {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.milestones(id),
    queryFn: () => apiFetch<Milestone[]>(`/v2/admin/ambassadors/${id}/milestones`),
    enabled: authenticated && !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useVerifyMilestone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ambassadorId, milestone }: { ambassadorId: string; milestone: string }) =>
      apiFetch(`/v2/admin/ambassadors/${ambassadorId}/milestones/${milestone}/verify`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_KEYS.all });
    },
  });
}

export function useApproveMilestone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ambassadorId, milestone }: { ambassadorId: string; milestone: string }) =>
      apiFetch(`/v2/admin/ambassadors/${ambassadorId}/milestones/${milestone}/approve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_KEYS.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Payout hooks
// ---------------------------------------------------------------------------

export function usePayouts(page: number = 1, status?: PayoutStatus) {
  const { authenticated } = useAuth();
  return useQuery({
    queryKey: AMBASSADOR_KEYS.payouts(page, status),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (status) params.set('status', status);
      return apiFetch<PayoutListResponse>(`/v2/admin/payouts?${params}`);
    },
    enabled: authenticated,
    staleTime: 2 * 60 * 1000,
  });
}

export function useUpdatePayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { status: PayoutStatus; notes?: string; processedBy?: string };
    }) =>
      apiFetch<Payout>(`/v2/admin/payouts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambassador-payouts'] });
    },
  });
}
